import { useState, useEffect } from 'react'
import Header from '../components/Header'

interface LogEntry {
  id: string
  timestamp: string
  level: 'INFO' | 'WARN' | 'ERROR' | 'SUCCESS'
  category: string
  message: string
  details?: any
  duration?: number
}

interface LogStats {
  totalLogs: number
  errorCount: number
  successCount: number
  avgResponseTime: number
  lastActivity: string
}

export default function Logs() {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [stats, setStats] = useState<LogStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(true)

  const fetchLogs = async () => {
    try {
      setLoading(true)
      
      // Fetch logs from API
      const logsResponse = await fetch('/api/logs')
      if (logsResponse.ok) {
        const logsData = await logsResponse.json()
        setLogs(logsData)
      }

      // Fetch log stats
      const statsResponse = await fetch('/api/logs/stats')
      if (statsResponse.ok) {
        const statsData = await statsResponse.json()
        setStats(statsData)
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error)
    } finally {
      setLoading(false)
    }
  }

  const clearLogs = async () => {
    if (confirm('Are you sure you want to clear all logs?')) {
      try {
        await fetch('/api/logs', { method: 'DELETE' })
        setLogs([])
        alert('✅ Logs cleared successfully')
      } catch (error) {
        alert('❌ Failed to clear logs')
      }
    }
  }

  const exportLogs = () => {
    const filteredLogs = getFilteredLogs()
    const csvContent = [
      'Timestamp,Level,Category,Message,Duration',
      ...filteredLogs.map(log => 
        `"${log.timestamp}","${log.level}","${log.category}","${log.message}","${log.duration || ''}"`
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `obayito-logs-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  useEffect(() => {
    fetchLogs()
    
    if (autoRefresh) {
      const interval = setInterval(fetchLogs, 5000) // Refresh every 5 seconds
      return () => clearInterval(interval)
    }
  }, [autoRefresh])

  const getFilteredLogs = () => {
    return logs.filter(log => {
      const matchesFilter = filter === 'all' || log.level === filter
      const matchesSearch = searchTerm === '' || 
        log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.category.toLowerCase().includes(searchTerm.toLowerCase())
      
      return matchesFilter && matchesSearch
    })
  }

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'SUCCESS': return '#10b981'
      case 'INFO': return '#3b82f6'
      case 'WARN': return '#f59e0b'
      case 'ERROR': return '#ef4444'
      default: return '#6b7280'
    }
  }

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'SUCCESS': return '✅'
      case 'INFO': return 'ℹ️'
      case 'WARN': return '⚠️'
      case 'ERROR': return '❌'
      default: return '📝'
    }
  }

  const filteredLogs = getFilteredLogs()

  return (
    <div>
      <Header 
        title="Logs & Monitoring"
        subtitle="Monitor bot activities, errors, and performance metrics"
        actions={
          <>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              style={{
                padding: '0.5rem 1rem',
                background: autoRefresh ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              {autoRefresh ? '⏸️ Pause' : '▶️ Resume'} Auto-refresh
            </button>
            <button
              onClick={exportLogs}
              style={{
                padding: '0.5rem 1rem',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              📥 Export CSV
            </button>
            <button
              onClick={clearLogs}
              style={{
                padding: '0.5rem 1rem',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              🗑️ Clear Logs
            </button>
            <button
              onClick={fetchLogs}
              style={{
                padding: '0.5rem 1rem',
                background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              🔄 Refresh
            </button>
          </>
        }
      />

      <div style={{ padding: '2rem' }}>
        {/* Stats Cards */}
        {stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div style={{ padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#3b82f6' }}>📊 Total Logs</h3>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#3b82f6' }}>
                {stats.totalLogs}
              </p>
            </div>

            <div style={{ padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#ef4444' }}>❌ Errors</h3>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#ef4444' }}>
                {stats.errorCount}
              </p>
            </div>

            <div style={{ padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#10b981' }}>✅ Success</h3>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#10b981' }}>
                {stats.successCount}
              </p>
            </div>

            <div style={{ padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#f59e0b' }}>⚡ Avg Response</h3>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, color: '#f59e0b' }}>
                {stats.avgResponseTime}ms
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ 
          background: 'white', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ 
              padding: '0.5rem', 
              border: '1px solid #d1d5db', 
              borderRadius: '6px', 
              minWidth: '200px',
              flex: 1
            }}
          />
          
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ 
              padding: '0.5rem', 
              border: '1px solid #d1d5db', 
              borderRadius: '6px' 
            }}
          >
            <option value="all">All Levels</option>
            <option value="SUCCESS">Success</option>
            <option value="INFO">Info</option>
            <option value="WARN">Warning</option>
            <option value="ERROR">Error</option>
          </select>

          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Showing {filteredLogs.length} of {logs.length} logs
            {autoRefresh && <span style={{ color: '#10b981' }}> • Auto-refreshing</span>}
          </div>
        </div>

        {/* Logs Table */}
        <div style={{ 
          background: 'white', 
          borderRadius: '8px', 
          overflow: 'hidden', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)' 
        }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
              Loading logs...
            </div>
          ) : filteredLogs.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
              No logs found matching your criteria.
            </div>
          ) : (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {filteredLogs.map((log) => (
                <div
                  key={log.id}
                  style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    minWidth: '120px'
                  }}>
                    <span style={{ fontSize: '1.25rem' }}>
                      {getLevelIcon(log.level)}
                    </span>
                    <span
                      style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        color: 'white',
                        background: getLevelColor(log.level)
                      }}
                    >
                      {log.level}
                    </span>
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.25rem' }}>
                      <span style={{ 
                        fontSize: '0.875rem', 
                        color: '#6b7280',
                        fontFamily: 'monospace'
                      }}>
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                      <span style={{
                        padding: '0.125rem 0.5rem',
                        background: '#f3f4f6',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {log.category}
                      </span>
                      {log.duration && (
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {log.duration}ms
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.95rem', color: '#1f2937' }}>
                      {log.message}
                    </div>
                    {log.details && (
                      <details style={{ marginTop: '0.5rem' }}>
                        <summary style={{ 
                          cursor: 'pointer', 
                          fontSize: '0.875rem', 
                          color: '#6b7280' 
                        }}>
                          Show details
                        </summary>
                        <pre style={{ 
                          marginTop: '0.5rem',
                          padding: '0.75rem',
                          background: '#f9fafb',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          maxHeight: '200px'
                        }}>
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}